import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Typography, Spin, message } from 'antd';
import {
  BankOutlined,
  DollarOutlined,
  TeamOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title as ChartTitle,
  Tooltip,
  Legend
} from 'chart.js';
import api from '../../services/api';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  ChartTitle,
  Tooltip,
  Legend
);

const { Title } = Typography;

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [packageData, setPackageData] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      const [dashRes, revRes, pkgRes] = await Promise.allSettled([
        api.get('/analytics/dashboard'),
        api.get('/analytics/revenue'),
        api.get('/analytics/packages')
      ]);

      if (dashRes.status === 'fulfilled' && dashRes.value.data.success) {
        setDashboardStats(dashRes.value.data.data.stats);
      }

      if (revRes.status === 'fulfilled' && revRes.value.data.success) {
        setRevenueData(revRes.value.data.data);
      }

      if (pkgRes.status === 'fulfilled' && pkgRes.value.data.success) {
        setPackageData(pkgRes.value.data.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      message.warning('Some analytics data could not be loaded');
    } finally {
      setLoading(false);
    }
  };

  const revenueChartData = {
    labels: revenueData?.monthlyRevenue?.map(r => r.month) || ['No Data'],
    datasets: [
      {
        label: 'Revenue (₹)',
        data: revenueData?.monthlyRevenue?.map(r => parseFloat(r.revenue)) || [0],
        backgroundColor: 'rgba(24, 144, 255, 0.6)',
        borderColor: '#1890ff',
        borderWidth: 1,
        borderRadius: 4
      }
    ]
  };

  const packageChartData = {
    labels: packageData?.packageDistribution?.map(p => p.Package?.name || 'Unknown') || ['No Data'],
    datasets: [
      {
        data: packageData?.packageDistribution?.map(p => parseInt(p.companyCount)) || [0],
        backgroundColor: [
          '#1890ff',
          '#52c41a',
          '#faad14',
          '#722ed1',
          '#eb2f96',
          '#13c2c2'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Spin size="large" tip="Loading analytics..." />
      </div>
    );
  }

  return (
    <div>
      <Title level={3} style={{ marginBottom: 24 }}>Analytics</Title>

      {/* Summary Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Companies"
              value={dashboardStats?.totalCompanies || 0}
              prefix={<BankOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Revenue"
              value={dashboardStats?.totalRevenue || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Employees"
              value={dashboardStats?.totalEmployees || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Expiring Soon"
              value={dashboardStats?.expiringCompanies || 0}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title="Monthly Revenue" style={{ borderRadius: 8 }}>
            <div style={{ height: 300 }}>
              <Bar
                data={revenueChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: (value) => `₹${value.toLocaleString()}`
                      }
                    }
                  }
                }}
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="Package Distribution" style={{ borderRadius: 8 }}>
            <div style={{ height: 300, display: 'flex', justifyContent: 'center' }}>
              <Doughnut
                data={packageChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom'
                    }
                  }
                }}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Analytics;
